/*
 * Product card hover slideshow.
 *
 * Cycles `product.media` images on hover, restarting the slow CSS zoom
 * animation each tick. No DOM duplication — a real interval drives the
 * advance, looping the actual slide list. Single-photo products still
 * loop with the same slide (zoom restarts + crossfade is clean).
 *
 * Activation: any element with `[data-slideshow]` containing child
 *   `.productitem--slide` elements (each with `data-slide-index`).
 * Trigger: `mouseenter` / `mouseleave` on the nearest .productgrid--item
 *   (falls back to .productitem if no grid wrapper).
 *
 * Timing: each slide is the visible "active" one for 3000ms (matches
 *   the CSS `productitem-zoom` animation duration). On mouseleave we
 *   reset to slide 0 in its static state.
 */
(function () {
  'use strict';

  var CYCLE_MS = 3000;
  var ATTR_INITIALIZED = 'data-slideshow-initialized';

  var FADE_MS = 700;
  var exitTimers = new WeakMap();

  function lockExitingTransform(slide) {
    // Capture the slide's CURRENT computed transform (mid-animation value)
    // and apply it inline. This pins the scale so when we remove
    // data-active the CSS animation rule no longer applies, but the
    // inline transform keeps the slide at its last animated scale —
    // the visual snap-back-to-scale(1) is gone.
    var computed = window.getComputedStyle(slide).transform;
    if (computed && computed !== 'none') {
      slide.style.transform = computed;
    }
    slide.style.animation = 'none';
    slide.removeAttribute('data-active');
    slide.setAttribute('data-exiting', 'true');

    // After the fade-out completes, clear the inline transform + exiting
    // flag so the slide is fully reset for its next activation.
    var existing = exitTimers.get(slide);
    if (existing) window.clearTimeout(existing);
    var timer = window.setTimeout(function () {
      slide.removeAttribute('data-exiting');
      slide.style.transform = '';
      slide.style.animation = '';
      exitTimers.delete(slide);
    }, FADE_MS);
    exitTimers.set(slide, timer);
  }

  function activate(slides, idx) {
    for (var i = 0; i < slides.length; i++) {
      var slide = slides[i];
      if (i === idx) {
        // Cancel any pending exit cleanup for this slide and reset
        // any inline transform / exiting state so the animation runs
        // cleanly from scale(1).
        var pending = exitTimers.get(slide);
        if (pending) {
          window.clearTimeout(pending);
          exitTimers.delete(slide);
        }
        slide.removeAttribute('data-exiting');
        slide.style.transform = '';

        // Force animation restart so the slide zooms from scale(1)
        // every time it becomes active (not from a stale frame).
        slide.style.animation = 'none';
        // eslint-disable-next-line no-unused-expressions
        slide.offsetWidth;
        slide.style.animation = '';
        slide.setAttribute('data-active', 'true');
      } else if (slide.hasAttribute('data-active')) {
        // Was active, now becoming inactive: lock its current scale
        // and start the fade-out.
        lockExitingTransform(slide);
      }
    }
  }

  function reset(slides) {
    for (var i = 0; i < slides.length; i++) {
      var slide = slides[i];
      var pending = exitTimers.get(slide);
      if (pending) {
        window.clearTimeout(pending);
        exitTimers.delete(slide);
      }
      slide.removeAttribute('data-active');
      slide.removeAttribute('data-exiting');
      slide.style.animation = '';
      slide.style.transform = '';
    }
  }

  function attach(figure) {
    if (figure.hasAttribute(ATTR_INITIALIZED)) return;
    figure.setAttribute(ATTR_INITIALIZED, 'true');

    var slides = figure.querySelectorAll('.productitem--slide');
    if (slides.length === 0) return;

    // Find the hover target — prefer the outer grid item so the hover
    // area includes the info column + ATC button (otherwise the
    // slideshow stops the moment the cursor leaves the image).
    var hoverTarget = figure.closest('.productgrid--item');
    if (!hoverTarget) hoverTarget = figure.closest('.productitem');
    if (!hoverTarget) hoverTarget = figure;

    var timer = null;
    var currentIndex = 0;

    var refadeTimer = null;

    function start() {
      // Skip on touch devices — hover events fire weirdly there and
      // the cycle would be locked on after a tap.
      if (window.matchMedia && window.matchMedia('(hover: none)').matches) {
        return;
      }
      figure.classList.add('is-cycling');
      currentIndex = 0;
      activate(slides, currentIndex);
      timer = window.setInterval(function () {
        if (slides.length === 1) {
          // Single-image product: loop with itself via fade-out → fade-in
          // on the SAME slide. Lock the current zoomed transform, fade
          // opacity to 0, then re-activate (which fades opacity back to
          // 1 and restarts the zoom from scale(1)). No DOM duplication.
          lockExitingTransform(slides[0]);
          if (refadeTimer) window.clearTimeout(refadeTimer);
          refadeTimer = window.setTimeout(function () {
            refadeTimer = null;
            activate(slides, 0);
          }, FADE_MS);
        } else {
          currentIndex = (currentIndex + 1) % slides.length;
          activate(slides, currentIndex);
        }
      }, CYCLE_MS);
    }

    function stop() {
      if (timer !== null) {
        window.clearInterval(timer);
        timer = null;
      }
      if (refadeTimer !== null) {
        window.clearTimeout(refadeTimer);
        refadeTimer = null;
      }
      figure.classList.remove('is-cycling');
      reset(slides);
      currentIndex = 0;
    }

    hoverTarget.addEventListener('mouseenter', start);
    hoverTarget.addEventListener('mouseleave', stop);
    // Pause when the tab is hidden — saves work and prevents the cycle
    // from drifting while the user is away.
    document.addEventListener('visibilitychange', function () {
      if (document.hidden && timer !== null) {
        stop();
      }
    });
  }

  function initAll(root) {
    var scope = root || document;
    var figures = scope.querySelectorAll('[data-slideshow]:not([' + ATTR_INITIALIZED + '])');
    for (var i = 0; i < figures.length; i++) {
      attach(figures[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // Re-init on Shopify section reloads (theme editor) and infinite-scroll
  // / faceted-filter re-renders that bring new cards into the DOM.
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target || document);
  });

  // Generic fallback — observe the body for new cards.
  if (typeof MutationObserver !== 'undefined') {
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches('[data-slideshow]')) {
            attach(node);
          } else if (node.querySelectorAll) {
            initAll(node);
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }
})();
